import { useMemo } from 'react'
import { useParams } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { httpBatchLink } from '@trpc/client'
import env from '@/config/env'
import { trpc } from '@/config/trpc'
import { SharedViewProvider } from '@/context/sharedView'
import { ScheduledPostsPage } from '@/pages/scheduled-page/scheduled-posts-page'

// Standalone tRPC + Query clients scoped to the shared route. Keeps the share
// token out of the main app client (which has a refresh-on-UNAUTHORIZED link
// that would otherwise log the actual app user out if a share token expires).
export function SharedViewPage() {
  const { token } = useParams<{ token: string }>()

  const { queryClient, trpcClient } = useMemo(() => {
    const qc = new QueryClient()
    const tc = trpc.createClient({
      links: [
        httpBatchLink({
          url: env.VITE_BACKEND_URL,
          headers: () => ({ authorization: `Bearer ${token ?? ''}` }),
        }),
      ],
    })
    return { queryClient: qc, trpcClient: tc }
  }, [token])

  if (!token) return <InvalidShareLink />

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <SharedViewProvider token={token}>
          <SharedViewBody />
        </SharedViewProvider>
      </QueryClientProvider>
    </trpc.Provider>
  )
}

// Validity probe runs inside the providers so it uses the shared-token client.
// Gates rendering of the actual page — failing fast on a revoked/expired link
// avoids the awkward "empty calendar" that would otherwise appear when every
// downstream query silently 401s.
function SharedViewBody() {
  const probe = trpc.brand.getDisplay.useQuery(undefined, { retry: false })

  if (probe.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="border-primary/30 border-t-primary h-7 w-7 animate-spin rounded-full border-2" />
      </div>
    )
  }

  if (probe.error) return <InvalidShareLink />

  return (
    <div className="bg-background min-h-screen">
      <ScheduledPostsPage />
    </div>
  )
}

function InvalidShareLink() {
  return (
    <div className="flex h-screen items-center justify-center px-6">
      <div className="max-w-sm text-center">
        <h1 className="text-foreground text-lg font-semibold">
          This share link is no longer active
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          The link may have been revoked or replaced. Reach out to whoever shared it with you for an
          updated one.
        </p>
      </div>
    </div>
  )
}
